import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import ReactMarkdown from 'react-markdown';
import { GetResourceGroupFn, GetResourcePageFn } from '../../data/apiCalls';
import PageBanner from '../shared/Pages/PageBanner';
import PageBreadcrumbs from '../shared/Pages/PageBreadcrumbs';
import PageMobileTitle from '../shared/Pages/PageMobileTitle';
import MarkdownLinkRenderer from '../shared/MarkdownLinkRenderer';
import MarkdownParagraphRenderer from '../shared/MarkdownParagraphRenderer';
import { getResourceHeader, isProvider } from '../../scripts/resourcesHelpers';
import { TOP_LEVEL_ROUTES } from '../../scripts/routes';
import useIsMobile from '../../hooks/useIsMobile';
import PageNotFound from '../Errors/PageNotFound';
import PageFailure from '../Errors/PageFailure';
import ResourcePageSkeleton from './ResourcePage.skeleton';

const ResourcePage = () => {
	const { groupPageId, pageId } = useParams();
	const groupPageHeader = getResourceHeader(groupPageId);
	const isMobile = useIsMobile();

	const groupData = useQuery(
		['ProviderResourceGroup', groupPageId],
		() => GetResourceGroupFn(groupPageId),
		{
			staleTime: Infinity,
		}
	);
	const pageData = useQuery(
		['ProviderResourcePage', pageId],
		() => GetResourcePageFn(pageId),
		{
			staleTime: Infinity,
		}
	);

	const group = {
		loading: groupData.isLoading,
		error: groupData.isError,
		status: groupData.data?.status,
		resultCode: groupData.data?.data?.resultCode,
		subResultCode: groupData.data?.data?.subResultCode,
		data: groupData.data?.data?.data?.result,
	};
	const page = {
		loading: pageData.isLoading,
		error: pageData.isError,
		status: pageData.data?.status,
		resultCode: pageData.data?.data?.resultCode,
		subResultCode: pageData.data?.data?.subResultCode,
		data: pageData.data?.data?.data?.resourcePage,
	};

	const providerCrumbs = [
		groupPageHeader,
		{
			link: `/${TOP_LEVEL_ROUTES.resources}/${groupPageId}`,
			text: group.data?.displayName,
		},
		{ link: '', text: page.data?.displayName },
	];
	const crumbs = [
		groupPageHeader,
		{ link: '', text: page.data?.displayName },
	];

	const errorGroup =
		group.error ||
		(group.status >= 400 && group.status <= 505) ||
		group.resultCode !== 0;
	const errorPage =
		page.error ||
		(page.status >= 400 && page.status <= 505) ||
		page.resultCode !== 0;
	const error404 =
		group.status === 404 ||
		group.subResultCode === 9001 ||
		page.status === 404 ||
		page.subResultCode === 9001;
	const hideBanner = errorGroup && errorPage;

	const renderContent = () => {
		if (error404) return <PageNotFound />;
		return (
			<>
				{isMobile ? (
					<PageMobileTitle title={groupPageHeader.text} />
				) : (
					!errorGroup &&
					!errorPage && (
						<PageBreadcrumbs
							crumbs={
								isProvider(groupPageId)
									? providerCrumbs
									: crumbs
							}
						/>
					)
				)}
				{!hideBanner && (
					<PageBanner
						error={errorGroup}
						imageUrl={group.data?.bannerImageUrl}
						title={groupPageHeader.text}
						subtitle={group.data?.displayName}
					/>
				)}
				{errorPage ? (
					<PageFailure />
				) : (
					<Box sx={{ my: { xs: 2, md: 4 } }}>
						<Typography
							variant='h4'
							component='h1'
							data-testid='resource-page-title'
						>
							{page.data?.displayName}
						</Typography>
						<Divider sx={{ my: { xs: 2, md: 3 } }} />
						<Box sx={{ mb: { xs: 2, md: 3 } }}>
							<ReactMarkdown
								components={{
									a: MarkdownLinkRenderer,
									p: MarkdownParagraphRenderer,
								}}
							>
								{page.data?.overview}
							</ReactMarkdown>
						</Box>
					</Box>
				)}
			</>
		);
	};

	return (
		<Container>
			{group.loading || page.loading ? (
				<ResourcePageSkeleton />
			) : (
				renderContent()
			)}
		</Container>
	);
};

export default ResourcePage;
